import { DownloadOutlined, WarningOutlined, SwapOutlined, LoadingOutlined } from '@ant-design/icons';
import { Button, Table, Tooltip, Dropdown, Menu, Alert } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import { Observer, observer } from 'mobx-react-lite';
import * as React from 'react';
import { Utils } from '../../../common/services/Utils';
import { TestProjectDashboardStore, TestProjectRunCompareStore } from '../../stores';
import { TestProjectRunResults } from '../../types';
import { TestProjectUtils } from '../../misc';
import TestProjectDuration from '../TestProjectDuration';
import './TestProjectRuns.less';

type Props = {
    store: TestProjectDashboardStore;
    runCompareStore: TestProjectRunCompareStore;
};

const TestProjectRuns: React.FC<Props> = ({ store, runCompareStore }) => {
    React.useEffect(() => {
        if (store.currentProject && store.currentTestProjectId) {
            store.loadTestRuns();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [store.currentProject, store.currentTestProjectId]);

    React.useEffect(() => {
        return () => runCompareStore.abort();
    }, [runCompareStore]);

    const renderRunCompareMenu = (run: TestProjectRunResults) => (
        <Menu>
            <Menu.Item
                key="previous"
                onClick={() => runCompareStore.compareWithPreviousRun(run)}
                disabled={!runCompareStore.getPreviousRun(run)}
            >
                Compare with previous
            </Menu.Item>
            <Menu.Item key="selected" onClick={() => runCompareStore.compareWithSelectedRun(run)}>
                Compare with selected
            </Menu.Item>
        </Menu>
    );

    const renderActions = (run: TestProjectRunResults) => {
        if (run.id === 'inProgress' || runCompareStore.isSelectionMode) {
            return <></>;
        }

        const isSelectedForComparison = runCompareStore.selectedRunIds.has(run.id);
        const isLoading = isSelectedForComparison && runCompareStore.isLoading;
        const isDisabled = !isSelectedForComparison && runCompareStore.isLoading;

        return (
            <div data-id-cells="actions" style={{ minWidth: 100 }} onClick={e => e.stopPropagation()}>
                {isDisabled ? (
                    <Button icon={<SwapOutlined />} type="link" disabled />
                ) : (
                    <Dropdown
                        placement="topLeft"
                        overlay={renderRunCompareMenu(run)}
                        trigger={['click']}
                        disabled={isLoading}
                        arrow
                    >
                        <Button icon={isLoading ? <LoadingOutlined spin /> : <SwapOutlined />} type="link" />
                    </Dropdown>
                )}

                <Tooltip title="Download test run report">
                    <Button
                        icon={<DownloadOutlined />}
                        type="link"
                        onClick={() => store.exportTestProjectRunToExcel(run.id)}
                    />
                </Tooltip>
            </div>
        );
    };

    const testRunsContent = () => {
        const columns: ColumnProps<TestProjectRunResults>[] = [
            {
                title: 'Date time',
                dataIndex: 'runTime',
                defaultSortOrder: 'descend',
                sorter: (a: TestProjectRunResults, b: TestProjectRunResults) =>
                    Utils.safeDateStringCompare(a.runTime, b.runTime),
                render: (runTime: string, run: TestProjectRunResults) =>
                    run.id !== 'inProgress' ? (
                        <Button
                            style={{ marginLeft: -16 }}
                            type="link"
                            className="link-button"
                            onClick={() => store.goToTestResults(run.id)}
                        >
                            {Utils.formatDateStringShort(runTime)}
                        </Button>
                    ) : (
                        <span style={{ color: '#B80C02' }}>Running...</span>
                    )
            },
            {
                title: 'Duration',
                dataIndex: 'duration',
                defaultSortOrder: 'descend',
                render: (runTime: string, run: TestProjectRunResults) => (
                    <TestProjectDuration startTime={run.runTime} endTime={run.endTime} />
                )
            },
            {
                title: 'Result vs. Baseline',
                dataIndex: 'result',
                render: (_: unknown, run: TestProjectRunResults) =>
                    run.id !== 'inProgress' ? `${TestProjectUtils.calculateResultVsBaseline(run)}%` : null
            },
            {
                dataIndex: 'error',
                key: 'error',
                render: (error: string) =>
                    error ? (
                        <Tooltip title={error}>
                            <WarningOutlined style={{ color: 'red' }} />
                        </Tooltip>
                    ) : null
            },
            {
                title: 'Ran by',
                dataIndex: 'userId',
                render: (_: unknown, run: TestProjectRunResults) =>
                    run.id !== 'inProgress' && !store.isLoadingUsers
                        ? run.userId
                            ? store.getUserNameById(run.userId)
                            : null
                        : null
            },
            {
                title: 'Actions',
                dataIndex: 'actions',
                render: (_: unknown, run: TestProjectRunResults) => <Observer>{() => renderActions(run)}</Observer>
            }
        ];

        const getRowSelection = () => {
            if (!runCompareStore.isSelectionMode) {
                return undefined;
            }

            return {
                selectedRowKeys: Array.from(runCompareStore.selectedRunIds),
                onSelect: (record: TestProjectRunResults) => runCompareStore.toggleRunSelection(record.id),
                getCheckboxProps: (record: TestProjectRunResults) => ({
                    disabled:
                        record.id === 'inProgress' ||
                        (runCompareStore.selectedRunIds.size >= 2 && !runCompareStore.selectedRunIds.has(record.id))
                }),
                hideSelectAll: true,
                columnWidth: 48
            };
        };

        return (
            <Observer>
                {() => (
                    <>
                        {runCompareStore.isSelectionMode && (
                            <div className="test-project-runs-selection-mode">
                                <Alert
                                    message={`Select exactly 2 test runs to compare (${runCompareStore.selectedRunIds.size} selected)`}
                                    type="info"
                                    showIcon
                                />
                                <Button
                                    className="selection-mode-cancel-button"
                                    onClick={() => runCompareStore.cancelSelectionMode()}
                                >
                                    Cancel
                                </Button>
                            </div>
                        )}

                        <Table
                            columns={columns}
                            dataSource={store.testRuns}
                            rowKey={r => r.id}
                            pagination={false}
                            className="alpha-portal-table test-project-runs-table"
                            loading={store.isLoadingTestRuns}
                            data-id="test-project-runs-table"
                            rowSelection={getRowSelection()}
                        />
                    </>
                )}
            </Observer>
        );
    };

    return (
        <div className="test-project-dashboard-item">
            <div className="item-title">Test runs</div>
            <div className="item-content runs-table">{testRunsContent()}</div>
        </div>
    );
};

export default observer(TestProjectRuns);
